/**
 * Reach HUD toasts (plan R15): short messages from reach events (charted, rumoured, POI revealed, chart value, parts
 * delivered, relay online, saved, save failed, …), read from the reach event ring with the toast queue's own cursor.
 *
 * - `max` visible slots, oldest first; an expired toast frees its slot and the ones below move up.
 * - Beyond the slots, up to `queue` toasts wait; when that is full the oldest routine waiting one is dropped.
 * - Chart value accrued for one system within `coalesceS` adds to the toast already showing or waiting.
 *
 * - M6 (plan P22): fleet alerts (`fleet-link/presence.ts` inbox) become toasts with their alert kept on the toast, so
 *   the view (P35) can offer *jump to* and *send wing* (`runAlertAction`).
 * - An **urgent** fleet alert (tone `bad`: an asset attacked, a ship or a captain lost) never waits behind routine
 *   toasts (D-459 bug 2): it goes to the front of the waiting queue (behind earlier urgent alerts only), the oldest
 *   visible routine toast is hurried into its fade-out so the alert shows within `fadeS`, and a full queue drops a
 *   routine toast rather than an urgent one.
 *
 * The per-frame path (`readToastEvents` with no new events, `stepToastsBy`) allocates nothing. A toast's text is built
 * once, when its event is read (a rare event, D-014).
 */
import { EVENT_MASK, firstReadable, type EventRing } from '../../combat/events'
import { alertAt, firstUnread, fleetAlertInbox, fleetAlertTone, type FleetAlertInbox } from '../../fleet-link/presence'
import { REV } from '../../reach/events'
import type { FleetAlert } from '../../strategic/types'
import type { ReachHudConfig } from './config'
import { TOAST_EVENTS, toastText, toastTone, type ToastTone, type ToastWords } from './labels'

export interface Toast {
  /** The reach event code, 0 for an empty entry. */
  type: number
  a: number
  b: number
  amount: number
  /** Seconds shown (visible slots) or 0 (waiting). */
  age: number
  text: string
  tone: ToastTone
  /** Bumped whenever the text changes (the view diffs on it). */
  version: number
  /** The fleet alert a `FLEET_ALERT_TOAST` carries (its actions: P35), null otherwise. */
  alert: FleetAlert | null
}

export interface ToastQueue {
  /** The reach event ring cursor (its own; plan §2.4). */
  cursor: number
  /** Visible toasts, oldest first; `count` of them are live. */
  readonly slots: Toast[]
  count: number
  /** Waiting toasts, a ring of `pending.length`. */
  readonly pending: Toast[]
  pendingHead: number
  pendingCount: number
  /** Toasts dropped because the waiting ring was full (debug). */
  dropped: number
  /** The fleet alert inbox cursor (its own, like `cursor`). */
  alertCursor: number
}

const TOASTABLE = new Uint8Array(64)
for (let i = 0; i < TOAST_EVENTS.length; i++) TOASTABLE[TOAST_EVENTS[i]] = 1

function emptyToast(): Toast {
  return { type: 0, a: 0, b: 0, amount: 0, age: 0, text: '', tone: 'info', version: 0, alert: null }
}

/** A new queue; it reads fleet alerts from `inbox` arriving after its creation (no replay of an earlier session's). */
export function createToastQueue(cfg: ReachHudConfig, inbox: FleetAlertInbox = fleetAlertInbox): ToastQueue {
  const slots: Toast[] = []
  for (let i = 0; i < cfg.toasts.max; i++) slots.push(emptyToast())
  const pending: Toast[] = []
  for (let i = 0; i < cfg.toasts.queue; i++) pending.push(emptyToast())
  return { cursor: 0, slots, count: 0, pending, pendingHead: 0, pendingCount: 0, dropped: 0, alertCursor: inbox.head }
}

/** Copies every field of `from` into `to` (allocation-free; the version always moves on so the view rewrites). */
function copyToast(to: Toast, from: Toast): void {
  to.type = from.type
  to.a = from.a
  to.b = from.b
  to.amount = from.amount
  to.age = from.age
  to.text = from.text
  to.tone = from.tone
  to.alert = from.alert
  to.version++
}

function fill(t: Toast, type: number, a: number, b: number, amount: number, text: string): void {
  t.type = type
  t.a = a
  t.b = b
  t.amount = amount
  t.age = 0
  t.text = text
  t.tone = toastTone(type, b)
  t.alert = null
  t.version++
}

/** Adds chart value to a showing or waiting chart toast for the same system; true when it did. */
function coalesce(q: ToastQueue, words: ToastWords, a: number, amount: number, cfg: ReachHudConfig): boolean {
  let t: Toast | null = null
  for (let i = q.count - 1; i >= 0 && t === null; i--) {
    const s = q.slots[i]
    if (s.type === REV.chartValue && s.a === a && s.age < cfg.toasts.coalesceS) t = s
  }
  for (let k = 0; k < q.pendingCount && t === null; k++) {
    const s = q.pending[(q.pendingHead + k) % q.pending.length]
    if (s.type === REV.chartValue && s.a === a) t = s
  }
  if (t === null) return false
  t.amount += amount
  t.text = toastText(words, REV.chartValue, a, 0, t.amount) ?? t.text
  t.version++
  return true
}

/** Queues one toast for an event (rare: builds its text). Returns false when the event is not toasted. */
export function pushToast(q: ToastQueue, words: ToastWords, type: number, a: number, b: number, amount: number, cfg: ReachHudConfig): boolean {
  if (type === REV.chartValue && coalesce(q, words, a, amount, cfg)) return true
  const text = toastText(words, type, a, b, amount)
  if (text === null) return false
  enqueue(q, type, a, b, amount, text)
  return true
}

/** Toast type for a subsystem notice (free text, e.g. exploration flavour; never a reach event code). */
export const NOTICE_TOAST = -1

/** Queues one free-text notice toast (rare: a subsystem notice such as the exploration flavour text). */
export function pushNoticeToast(q: ToastQueue, text: string): void {
  enqueue(q, NOTICE_TOAST, 0, 0, 0, text)
}

/** Toast type for a fleet alert (M6 P22; never a reach event code). */
export const FLEET_ALERT_TOAST = -2

/** True for an urgent fleet alert toast (tone `bad`: an asset attacked, a ship or a captain lost). */
function urgent(t: Toast): boolean {
  return t.type === FLEET_ALERT_TOAST && t.tone === 'bad'
}

/** Queues one fleet alert toast (rare): the alert's text and tone, the alert kept for its actions. Urgent ones jump the queue. */
export function pushFleetAlertToast(q: ToastQueue, alert: FleetAlert, cfg: ReachHudConfig): void {
  const tone = fleetAlertTone(alert.kind)
  const t = tone === 'bad' ? enqueueUrgent(q, alert.text, cfg) : enqueue(q, FLEET_ALERT_TOAST, 0, 0, 0, alert.text)
  t.tone = tone
  t.alert = alert
}

/** Reads every fleet alert since the queue's alert cursor into the queue. Allocation-free when none is new. */
export function readFleetAlertToasts(q: ToastQueue, inbox: FleetAlertInbox, cfg: ReachHudConfig): void {
  for (let i = firstUnread(inbox, q.alertCursor); i < inbox.head; i++) {
    const a = alertAt(inbox, i)
    if (a !== null) pushFleetAlertToast(q, a, cfg)
  }
  q.alertCursor = inbox.head
}

/** Removes waiting toast `j` (0 = the oldest), keeping the others in order. */
function removeWaiting(q: ToastQueue, j: number): void {
  const cap = q.pending.length
  for (let k = j; k > 0; k--) copyToast(q.pending[(q.pendingHead + k) % cap], q.pending[(q.pendingHead + k - 1) % cap])
  q.pending[q.pendingHead].type = 0
  q.pending[q.pendingHead].alert = null
  q.pendingHead = (q.pendingHead + 1) % cap
  q.pendingCount--
  q.dropped++
}

/** Makes room in a full waiting queue: drops the oldest routine toast (`newest`: the newest one), else the oldest. */
function dropWaiting(q: ToastQueue, newest: boolean): void {
  const cap = q.pending.length
  let pick = -1
  for (let k = 0; k < q.pendingCount; k++) {
    if (urgent(q.pending[(q.pendingHead + k) % cap])) continue
    pick = k
    if (!newest) break
  }
  removeWaiting(q, pick < 0 ? 0 : pick)
}

/**
 * Queues an urgent fleet alert toast: shown at once when a slot is free, else first in the waiting queue (after the
 * urgent ones already waiting) with the oldest visible routine toast hurried into its fade-out.
 */
function enqueueUrgent(q: ToastQueue, text: string, cfg: ReachHudConfig): Toast {
  if (q.pendingCount === 0 && q.count < q.slots.length) return enqueue(q, FLEET_ALERT_TOAST, 0, 0, 0, text)
  const cap = q.pending.length
  if (q.pendingCount === cap) dropWaiting(q, true)
  q.pendingHead = (q.pendingHead - 1 + cap) % cap
  q.pendingCount++
  // Earlier urgent alerts stay ahead of this one.
  let k = 0
  while (k + 1 < q.pendingCount && urgent(q.pending[(q.pendingHead + k + 1) % cap])) {
    copyToast(q.pending[(q.pendingHead + k) % cap], q.pending[(q.pendingHead + k + 1) % cap])
    k++
  }
  const t = q.pending[(q.pendingHead + k) % cap]
  fill(t, FLEET_ALERT_TOAST, 0, 0, 0, text)
  t.tone = 'bad'
  hurryRoutine(q, cfg)
  return t
}

/** Starts the fade-out of the oldest visible routine toast not already fading (none when every slot is urgent). */
function hurryRoutine(q: ToastQueue, cfg: ReachHudConfig): void {
  const out = cfg.toasts.seconds - cfg.toasts.fadeS
  for (let i = 0; i < q.count; i++) {
    const s = q.slots[i]
    if (urgent(s) || s.age >= out) continue
    s.age = out
    s.version++
    return
  }
}

function enqueue(q: ToastQueue, type: number, a: number, b: number, amount: number, text: string): Toast {
  if (q.pendingCount === 0 && q.count < q.slots.length) {
    const t = q.slots[q.count]
    fill(t, type, a, b, amount, text)
    q.count++
    return t
  }
  const cap = q.pending.length
  if (q.pendingCount === cap) dropWaiting(q, false)
  const t = q.pending[(q.pendingHead + q.pendingCount) % cap]
  fill(t, type, a, b, amount, text)
  q.pendingCount++
  return t
}

/** Reads every reach event since the cursor into the queue, then moves the cursor to the head. */
export function readToastEvents(q: ToastQueue, ring: EventRing, words: ToastWords, cfg: ReachHudConfig): void {
  for (let i = firstReadable(ring, q.cursor); i < ring.head; i++) {
    const s = i & EVENT_MASK
    const type = ring.type[s]
    if (type < TOASTABLE.length && TOASTABLE[type] === 1) pushToast(q, words, type, ring.eid[s], ring.other[s], ring.amount[s], cfg)
  }
  q.cursor = ring.head
}

/** `stepToasts`'s dt slot, so it can hand dt to `stepToastsBy` without passing a double. */
const stepDt = new Float64Array(1)

/** Ages the visible toasts by `dt` seconds, drops the expired ones and moves waiting toasts into free slots. */
export function stepToasts(q: ToastQueue, dt: number, cfg: ReachHudConfig): void {
  stepDt[0] = dt
  stepToastsBy(q, stepDt, cfg)
}

/**
 * `stepToasts` with dt read from `dt[0]`. Allocation-free: the per-frame caller (the HUD model) keeps dt in a typed
 * array, because a fractional double passed to a call V8 does not inline is boxed (16 B a frame; D-TBD heap sweep).
 */
export function stepToastsBy(q: ToastQueue, dt: Float64Array, cfg: ReachHudConfig): void {
  const d = dt[0]
  for (let i = 0; i < q.count; i++) q.slots[i].age += d
  // Drop expired toasts from the top (the oldest expire first; a coalesced one may outlive its elders).
  let i = 0
  while (i < q.count) {
    if (q.slots[i].age < cfg.toasts.seconds) {
      i++
      continue
    }
    for (let k = i; k < q.count - 1; k++) copyToast(q.slots[k], q.slots[k + 1])
    q.count--
    const last = q.slots[q.count]
    last.type = 0
    last.text = ''
    last.alert = null
    last.version++
  }
  while (q.pendingCount > 0 && q.count < q.slots.length) {
    copyToast(q.slots[q.count], q.pending[q.pendingHead])
    q.slots[q.count].age = 0
    q.pending[q.pendingHead].type = 0
    q.pending[q.pendingHead].alert = null
    q.pendingHead = (q.pendingHead + 1) % q.pending.length
    q.pendingCount--
    q.count++
  }
}

/** A visible toast's opacity: a fade in and a fade out of `fadeS` each. */
export function toastOpacity(t: Toast, cfg: ReachHudConfig): number {
  const f = cfg.toasts.fadeS
  if (f <= 0) return 1
  const rest = cfg.toasts.seconds - t.age
  const o = t.age < f ? t.age / f : rest < f ? rest / f : 1
  return o < 0 ? 0 : o > 1 ? 1 : o
}
